/**
 * shared/lib/security-posture/tier-ladder/moves-types.ts
 *
 * `LadderMoves` and its companion input/fact types (AC-25, spec
 * `2026-09-04-security-ladder-board` module 02 §1, PR A Task 3) — split out
 * of `./moves` (300-line modularity rule, fix round 1): the guardian-only
 * short-circuit and the guardian-dedupe fixes pushed the derivation past the
 * cap once combined with its own type declarations. `./moves`'s
 * `ladderMoves()` is the pure derivation that produces this shape;
 * `./moves-guard`'s `isLadderMoves()` is its runtime validator.
 *
 * Types, plus the two closed-vocabulary TUPLES those types are derived from
 * (final fix round, code-review Important 3): `./moves-guard` builds its
 * runtime Sets from these same tuples rather than restating the members, so
 * a value added to a union here can never type-check while silently failing
 * wire validation. No logic, no I/O.
 */

import type { HardwareRoute } from "../hardware-route";
import type { MemorySetSummary } from "../../sufficiency/memory-set";
import type { AtomAxisId } from "../../bespoke-seed/atom-axes";
import type { LadderAccountProfile } from "./ceiling";
import type { AccountCeilingAdmission } from "./admission";
import type { LadderCategorySwitches } from "./stars";

/** Whether a possession move is enrolled-and-mature, enrolled-and-waiting on
 *  its maturity window, or not yet enrolled. */
export type MoveState = "available" | "waiting" | "done";

/** Placement under Level 3, derived from `STAR_WEIGHT` vs
 *  `LEVEL_3_STAR_THRESHOLD` (AC-25) — category-cap aware. Never a number: the
 *  weight that produced this tag never crosses the wire. */
export type Reaches = "alone" | "with_another";

/** Whether the memory set is switched off for this account (`dark`), lit but
 *  not yet priced (`not_yet`), or priced on the ladder (`counts`) — the
 *  closed tuple `./moves-guard` narrows the wire value against. */
export const MEMORY_LADDER_STATES = ["dark", "not_yet", "counts"] as const;

/** Derived from `MEMORY_LADDER_STATES` — never a second hand-typed list. */
export type MemoryLadderState = (typeof MEMORY_LADDER_STATES)[number];

/** The recovery net's two states, as a closed tuple (same single-source rule
 *  as `MEMORY_LADDER_STATES` above). */
export const RECOVERY_NET_STATES = ["ready", "not_ready"] as const;

/** Derived from `RECOVERY_NET_STATES` — never a second hand-typed list. */
export type RecoveryNetState = (typeof RECOVERY_NET_STATES)[number];

/** The Level-4 recovery net's closed blocker vocabulary (module 02 §5) — a
 *  state, never a count; no key carries a number. */
export type RecoveryNetBlocker =
  | "guardian_not_yet_accepted"
  | "guardian_still_new"
  | "review_due"
  | "no_trusted_people"
  | "needs_more";

/** The moves that reach Level 3, plus the Level 4 recovery net (module 02
 *  §1). `guardianCheck`/`recoveryNet` are OPTIONAL members, absent (never
 *  `null`) while their category/kill-switch is dark. */
export interface LadderMoves {
  secondPasskey: {
    state: MoveState;
    reaches: Reaches;
    activeCredentials: number;
    maturesAtMs: number | null;
    /** `focusCredentialId` (AC-31): the credential a `done` move was
     *  satisfied by, for the Security Keys tab's `?focus=` link
     *  (DESIGN-SYSTEM §8). Optional and nullable — absent (an older
     *  reading) or `null` (nothing resolvable, the `done` gate below, or
     *  the guardian-only pin) all mean the row renders with no chevron,
     *  never an enrolment link. `ladderMoves()` copies this straight from
     *  `LadderMoveFacts` ONLY while this move reads `done`, and publishes
     *  `null` otherwise (final fix round, security F-001): a move that
     *  claims nothing is done has no `?focus=` row to point at, so
     *  carrying the id would be data the reading has no use for (module 02
     *  §7). It is never derived or invented here. */
    focusCredentialId?: string | null;
  };
  /** `route` is the shared five-valued `HardwareRoute` (AC-26). */
  hardwareKey: {
    route: HardwareRoute;
    state: MoveState;
    reaches: Reaches;
    maturesAtMs: number | null;
    /** Same `?focus=` convention as `secondPasskey.focusCredentialId` above. */
    focusCredentialId?: string | null;
  };
  memories: {
    ladder: MemoryLadderState;
    reaches: Reaches;
    saved: number;
    confirmed: number;
    kindsCovered: number;
    byKind: Record<AtomAxisId, number>;
  };
  /** Absent while `switches.guardiansEnabled` is false. */
  guardianCheck?: {
    reaches: Reaches;
    guardians: { guardianId: string; label: string; questionsSet: boolean }[];
    done: boolean;
  };
  /** Absent while `admission.guardianKillSwitchEnabled` is false. */
  recoveryNet?: { state: RecoveryNetState; blockers: RecoveryNetBlocker[] };
}

/** One guardian's DISPLAY facts (module 02 §5) — richer than
 *  `LadderRecoveryGuardianFact` (`./admission`), which carries no `pending`
 *  concept. `pending` = invited, not yet accepted; `activatedAtMs` is
 *  clamped against the SAME guardian cutoff `accountCeiling` uses. */
export interface LadderMoveGuardianFact {
  guardianId: string;
  label: string;
  active: boolean;
  pending: boolean;
  activatedAtMs: number | null;
}

/** The facts `ladderMoves` needs beyond `LadderAccountProfile` — Task 4's
 *  `move-facts.ts` reads these server-side (module 02 §2). */
export interface LadderMoveFacts {
  /** AC-26 — `resolveHardwareRoute` over the account's credential rows. */
  hardwareRoute: HardwareRoute;
  /** `memorySetSummary()` (`../../sufficiency`) over the account's bank. */
  memory: MemorySetSummary;
  /** Every guardian connection the holder has, active or pending. */
  guardians: readonly LadderMoveGuardianFact[];
  /** Distinct guardian ids with a completed shared-question set
   *  (`guardianSharedQuestionLinks`) — always empty until Stage 3. */
  questionSetGuardianIds: readonly string[];
  /** `isReviewDue` (`server/logic/guardian/review-cycle.ts`). */
  reviewDue: boolean;
  /** AC-31 — the SECOND-OLDEST active credential's `credentialId`, the same
   *  ordering `ladder-composer/facts.ts`'s `secondPasskeyActivatedAtMs`
   *  documents (`byOldestFirst`, ascending `createdAt`, unreadable last).
   *  `null` when fewer than two credentials exist. Optional so a fixture
   *  predating this field keeps compiling — every real reader
   *  (`readLadderMoveFacts`) always sets it. */
  secondPasskeyFocusCredentialId?: string | null;
  /** AC-31 — the OLDEST attested, non-revoked credential's `credentialId`,
   *  the same ordering `ladder-composer/facts.ts`'s
   *  `attestedHardwareKeyActivatedAtMs` documents. `null` when none exist.
   *  Optional for the same fixture-compatibility reason as above. */
  attestedHardwareKeyFocusCredentialId?: string | null;
}

/** The exact `{profile, admission, switches}` slice of `LadderPosture.
 *  inputs` `accountCeiling` itself consumes — never the full server
 *  `LadderPosture` (shared/ may not import server/). */
export interface LadderMoveInputs {
  profile: LadderAccountProfile;
  admission: AccountCeilingAdmission;
  switches: LadderCategorySwitches;
}
